/**
 * Maps modern section ids to the report field paths used for content
 * resolution and SNT-5 fallbacks.
 */
import type { ReportTypeId } from '../config/reportTypes';

export const SECTION_FIELD_PATHS: Partial<Record<ReportTypeId, Record<string, string>>> = {
  // BTM-3 (v1.2.0) — feasibility historically resolved each section by
  // reading the report property of the same name (so no explicit map was
  // needed). The capacity-credit deep-dive is the one feasibility section
  // whose content lives UNDER `powerAnalysis.*` (shared shape with
  // power-availability), so it needs an explicit path entry. Other
  // feasibility sections continue to resolve by name (no entry required).
  feasibility: {
    capacityCreditDeepDive: 'powerAnalysis.capacityCreditDeepDive',
    // STAGE 5.1 — the grid-screening half absorbed from power-availability.
    // Paths are IDENTICAL to the power entries below, deliberately: the two
    // types resolve the same content from the same place while both carry these
    // sections (commit 1), and nothing has to shift when power drops them
    // (commit 2). `powerAnalysis` already exists on FEASIBILITY_SCHEMA for the
    // folded ELCC deep-dive, so this widens an object rather than adding one.
    gridCapacity: 'powerAnalysis.substationAssessment',
    interconnectionQueue: 'powerAnalysis.interconnectionQueue',
    rateStructures: 'powerAnalysis.rateStructure',
    curtailmentRisk: 'powerAnalysis.curtailmentRisk',
    renewableIntegration: 'powerAnalysis.renewableIntegration',
    gridRedundancy: 'powerAnalysis.redundancyAssessment',
    // Phase 3 part B — the water screen absorbed from the retired water report.
    // Paths are IDENTICAL to the water entries below, deliberately.
    supplyAvailability: 'waterAnalysis.supplyAvailability',
    groundwater: 'waterAnalysis.groundwaterAssessment',
    waterRights: 'waterAnalysis.waterRightsAnalysis',
    droughtResilience: 'waterAnalysis.droughtResilience',
  },
  'power-availability': {
    powerStrategyContext: 'powerStrategyContext',
    gridCapacity: 'powerAnalysis.substationAssessment',
    capacityCreditDeepDive: 'powerAnalysis.capacityCreditDeepDive',
    interconnectionQueue: 'powerAnalysis.interconnectionQueue',
    curtailmentRisk: 'powerAnalysis.curtailmentRisk',
    rateStructures: 'powerAnalysis.rateStructure',
    renewableIntegration: 'powerAnalysis.renewableIntegration',
    gridRedundancy: 'powerAnalysis.redundancyAssessment',
    waterDuty: 'generationImpactProjection.waterResource',
    btmTechComparison: 'btmDetailedAnalysis',
    lcoeBreakdown: 'lcoeDetailedAnalysis',
    gridVsBtm: 'gridVsBtmComparison',
    powerRiskMatrix: 'powerAnalysis.risks',
    actionPlan: 'powerAnalysis.recommendations',
  },
  'water-resource': {
    supplyAvailability: 'waterAnalysis.supplyAvailability',
    groundwater: 'waterAnalysis.groundwaterAssessment',
    waterRights: 'waterAnalysis.waterRightsAnalysis',
    droughtResilience: 'waterAnalysis.droughtResilience',
    coolingByTech: 'waterAnalysis.coolingWaterRequirements',
    wastewaterStormwater: 'waterAnalysis.wastewaterManagement',
    conservation: 'waterAnalysis.waterConservation',
    totalWaterCost: 'waterAnalysis.recommendations',
    waterRiskMatrix: 'waterAnalysis.risks',
  },
  'test-fit': {
    parcelOverview: 'testFitAnalysis.siteConstraints',
    // QA 2026-06-10 (Holly Ridge dedup similarity-1.0) — topography previously
    // aliased `testFitAnalysis.siteConstraints`, so Parcel Overview and
    // Topography resolved byte-identical. Remapped to the dedicated
    // `testFitAnalysis.topography` narrative (TEST_FIT_SCHEMA field).
    topography: 'testFitAnalysis.topography',
    buildingPlacement: 'testFitAnalysis.buildingPlacement',
    setbackCompliance: 'testFitAnalysis.setbackCompliance',
    parkingAccess: 'testFitAnalysis.parkingAccess',
    stormwater: 'testFitAnalysis.stormwaterDetention',
    utilityRouting: 'testFitAnalysis.utilityRouting',
    phasedBuildout: 'testFitAnalysis.phaseLayout',
    // QA 2026-06-10 — maxCapacity and riskMatrix BOTH aliased
    // `testFitAnalysis.recommendations` (second similarity-1.0 collision).
    // maxCapacity now resolves the dedicated C3 sizing-ceiling narrative
    // (`maxCampusCapacity`, persisted in siteRagService; prompt directs
    // "sizing math, NOT action items"); riskMatrix resolves the real risk
    // register (`report.risks` — the same source the viewer's
    // RiskMatrixSection renders, composed by the SNT-5 test-fit
    // risk-register fallback when the model leaves it empty).
    maxCapacity: 'maxCampusCapacity',
    constructabilityAssessment: 'constructabilityAssessment',
    costRangeEstimate: 'costRangeEstimate',
    riskMatrix: 'risks',
  },
  interconnection: {
    fiberRings: 'interconnectionAnalysis.fiberAvailability',
    latencyModel: 'interconnectionAnalysis.latencyModeling',
    carrierDiversity: 'interconnectionAnalysis.carrierDiversity',
    ixProximity: 'interconnectionAnalysis.ixProximity',
    submarineCable: 'interconnectionAnalysis.submarineCable',
    networkResilience: 'interconnectionAnalysis.networkResilience',
    bandwidthCapacity: 'interconnectionAnalysis.recommendations',
    capexOpex: 'interconnectionAnalysis.edgeDeployment',
    connectivityRiskMatrix: 'interconnectionAnalysis.risks',
  },
  environmental: {
    nepaScreening: 'nepaScreening',
    ecologicalAssessment: 'ecologicalAssessment',
    waterResources: 'waterResources',
    airQualityEmissions: 'airQualityEmissions',
    floodNaturalHazards: 'floodNaturalHazards',
    culturalHistorical: 'culturalHistorical',
    landUseZoning: 'landUseZoning',
    noiseVisualImpact: 'noiseVisualImpact',
    mitigationPlan: 'mitigationPlan',
    alternativesAnalysis: 'alternativesAnalysis',
    cumulativeImpacts: 'cumulativeImpacts',
    publicParticipationPlan: 'publicParticipationPlan',
    permittingPathway: 'permittingPathway',
    environmentalRiskMatrix: 'environmentalRiskMatrix',
  },
  'public-sentiment': {
    executive: 'executive',
    stakeholderMap: 'stakeholderMap',
    perBtmSentiment: 'perBtmSentiment',
    themeDeepDives: 'themeDeepDives',
    trendAnalysis: 'trendAnalysis',
    comparableSiteBenchmark: 'comparableSiteBenchmark',
    mitigationPlaybook: 'mitigationPlaybook',
    sentimentResearchDetail: 'publicSentimentMeta',
    appendix: 'sentimentAppendix',
  },
  'supply-chain-risk': {
    procurementOverview: 'supplyChainAnalysis.procurementOverview',
    criticalPathProcurement: 'supplyChainAnalysis.criticalPathAssessment',
    supplyChainRisks: 'supplyChainAnalysis.supplyChainRiskSynthesis',
    supplierRiskRanking: 'supplyChainAnalysis.supplierStrategy',
    quoteComparisons: 'supplyChainAnalysis.quoteComparisonInsights',
    supplyChainRiskMatrix: 'supplyChainAnalysis.supplyChainRiskSynthesis',
    actionPlan: 'developmentPathway',
  },
  'executive-summary': {
    btmViability: 'btmViabilityAssessment',
    crossReportSynthesis: 'crossReportSynthesis',
    criticalRisks: 'executiveSummaryCriticalRisks',
    sourceReports: 'executiveSummarySourcesUsed',
    actionPlan: 'conclusion.nextActions',
  },
  'custom-brief': {
    projectContext: 'customBriefProjectContext',
    customBriefQuestions: 'customBriefAnswers',
  },
};

/** Resolve a dotted field path on a report record. */
export function getReportFieldValue(report: Record<string, unknown>, fieldPath: string): unknown {
  const parts = fieldPath.split('.');
  let cur: unknown = report;
  for (const p of parts) {
    if (cur == null || typeof cur !== 'object') return undefined;
    cur = (cur as Record<string, unknown>)[p];
  }
  return cur;
}

/** Set a dotted field path on a report record, creating parent objects as needed. */
export function setReportFieldValue(report: Record<string, unknown>, fieldPath: string, value: unknown): void {
  const parts = fieldPath.split('.');
  if (parts.length === 0) return;
  let cur: Record<string, unknown> = report;
  for (let i = 0; i < parts.length - 1; i++) {
    const key = parts[i]!;
    const next = cur[key];
    if (next == null || typeof next !== 'object' || Array.isArray(next)) {
      const created: Record<string, unknown> = {};
      cur[key] = created;
      cur = created;
    } else {
      cur = next as Record<string, unknown>;
    }
  }
  cur[parts[parts.length - 1]!] = value;
}

export function fieldPathForSection(reportType: ReportTypeId | undefined | null, sectionId: string): string | undefined {
  if (!reportType) return undefined;
  return SECTION_FIELD_PATHS[reportType]?.[sectionId];
}

export function hasSectionFieldContent(value: unknown): boolean {
  if (value == null) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === 'object') return Object.keys(value as object).length > 0;
  return true;
}
